
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Banner from "../Components/Banner";

const aiServices = [
  {
    title: "Conversational AI",
    description:
      "Create intelligent conversational experiences with AI-powered chatbots and virtual assistants that understand user intent, answer questions, and improve customer interactions.",
    path: "/automation-and-ai-agents/conversational-ai",
    image: "/assets/services/img1.png",
  },
  {
    title: "Machine Learning & ML Ops",
    description:
      "Build, deploy, and manage machine learning models with scalable ML pipelines, model monitoring, automated workflows, and reliable production operations.",
    path: "/automation-and-ai-agents/machine-learning-mlops",
    image: "/assets/services/img2.png",
  },
  {
    title: "Generative AI & LLM",
    description:
      "Leverage generative AI and large language models to create intelligent applications, automate content workflows, retrieve business knowledge, and unlock new possibilities.",
    path: "/automation-and-ai-agents/generative-ai-llm",
    image: "/assets/services/img3.png",
  },
  {
    title: "AI & Automation",
    description:
      "Streamline business operations with AI-powered automation, intelligent workflows, and connected systems that reduce repetitive tasks and improve operational efficiency.",
    path: "/automation-and-ai-agents/ai-automation",
    image: "/assets/services/img4.png",
  },
  {
    title: "Agentic AI Services",
    description:
      "Develop goal-driven AI agents that can reason, plan, use tools, and execute multi-step workflows to help businesses achieve meaningful outcomes.",
    path: "/automation-and-ai-agents/agentic-ai-services",
    image: "/assets/services/img5.png",
  },
];

const AutomationPage = () => {
  return (
    <>
      <Helmet>
        <title>Automation & Agentic AI Services | Mobikode</title>

        <meta
          name="description"
          content="Explore Mobikode's AI services, including Conversational AI, Machine Learning and MLOps, Generative AI and LLM, AI Automation, and Agentic AI solutions."
        />

        <meta
          name="keywords"
          content="Automation and Agentic AI, Conversational AI Services, Machine Learning Services, MLOps Solutions, Generative AI, LLM Development, AI Automation Services, Agentic AI Development, AI Solutions, Intelligent Automation"
        />

        <meta
          property="og:title"
          content="Automation & Agentic AI Services | Mobikode"
        />

        <meta
          property="og:description"
          content="Discover AI-powered solutions that automate workflows, enable intelligent conversations, scale machine learning, and deliver outcomes through agentic AI."
        />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mobikode" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Automation & Agentic AI Services | Mobikode"
        />

        <meta
          name="twitter:description"
          content="Explore Conversational AI, Machine Learning & MLOps, Generative AI & LLM, AI Automation, and Agentic AI services from Mobikode."
        />
      </Helmet>

      <div className="bg-[#FFFFFF]">
        {/* Hero Section - Same ServiceNow Design */}
        <section className="relative w-full h-[500px] lg:h-[630px] flex items-center bg-[#000814] overflow-hidden py-12 md:py-0">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/services/bg3.png"
              alt="AI automation, machine learning, and agentic AI solutions"
              className="w-full h-full object-cover"
            />

            <div
              style={{
                background:
                  "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)",
              }}
              className="hidden md:block absolute inset-0"
            />

            <div
              style={{
                background:
                  "linear-gradient(0deg, #000000 40%, rgba(0, 0, 0, 0.3) 100%)",
              }}
              className="block md:hidden absolute inset-0"
            />
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px] flex flex-col items-start justify-center h-full">
            <div className="w-full max-w-[500px] md:max-w-[700px] lg:max-w-[900px] mt-auto md:mt-0">
              <h1 className="text-white font-[600] text-[28px] sm:text-[36px] md:text-[50px] lg:text-[60px] font-sora leading-[1.2] lg:leading-[1.1] mb-[12px] lg:mb-[22px] drop-shadow-xl tracking-wide text-left">
                Automation & Agentic AI
              </h1>

              <p className="text-[#FFFFFF] max-w-[800px] w-full text-[14px] sm:text-[16px] md:text-[18px] leading-[20px] md:leading-[22px] lg:leading-[24px] font-graphik font-light tracking-wider text-left opacity-90 lg:opacity-100">
                Agents That Deliver Outcomes
              </p>
            </div>
          </div>
        </section>

        {/* AI Services - Same ServiceNow Card Layout */}
        <section className="lg:py-[100px] py-[60px]">
          <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-[20px]">
              {aiServices.map((service) => (
                <ServiceCard key={service.path} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing Banner */}
        <Banner
          title="Turn AI Innovation Into Real Outcomes"
          text="Discover intelligent automation, generative AI, machine learning, and agentic AI solutions designed to help your business work smarter and scale confidently."
        />
      </div>
    </>
  );
};

export default AutomationPage;

const ServiceCard = ({ service }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ y: 0 }}
      animate={{ y: isHovered ? -15 : 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Link
        to={service.path}
        aria-label={`Explore ${service.title}`}
        className="group outline-none relative block w-full h-[290px] rounded-[0px] overflow-hidden bg-[#ebeff3] transition-colors duration-500 hover:bg-[#01386A] focus:bg-[#01386A] active:bg-[#01386A] cursor-pointer"
      >
        {/* Right Background Decorative Image */}
        <div className="absolute top-0 right-[-5%] sm:right-0 w-[45%] sm:w-[40%] h-full pointer-events-none overflow-hidden flex items-center justify-end">
          <img
            src={service.image}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-contain object-right opacity-90 group-hover:opacity-20 group-focus:opacity-20 group-active:opacity-20 transition-all duration-500 transform group-hover:scale-105"
          />
        </div>

        {/* Card Content */}
        <div className="relative z-10 flex flex-col justify-between h-full w-[85%] sm:w-[75%] p-8 lg:p-8">
          <h3 className="text-[16px] lg:text-[20px] font-[600] font-sora text-[#01386A] leading-[1.2] transition-colors duration-500 group-hover:text-white group-focus:text-white group-active:text-white">
            {service.title}
          </h3>

          <p className="absolute top-[105px] sm:top-[115px] pr-5 text-[14px] lg:text-[16px] font-[500] leading-[1.6] font-graphik text-gray-200 opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 group-focus:opacity-100 group-focus:translate-y-0 group-active:opacity-100 group-active:translate-y-0">
            {service.description}
          </p>

          <div className="mt-auto flex items-center gap-2 text-[15px] font-sora font-[600] text-[#01386A] transition-opacity duration-300 group-hover:opacity-0 group-focus:opacity-0 group-active:opacity-0">
            <span>Read More</span>
            <ArrowUpRight size={18} className="text-[#ED6800]" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
